/**
 * The Run Explorer's turn model (task 1151, step 3): a pure derivation of what
 * happened in a run, turn by turn, from the run-detail response.
 *
 * Report: `project/reports/innovate-app-collaboration-run-explorer.md`.
 *
 * TWO GENERATIONS OF RUN. Task 1150 adds `turnIndex` to every journal entry, a
 * `skipped` entry for every step a branch routed away from, the branch's
 * `evaluations`, and the agent step's `schemaCheck`. A run recorded before that
 * has none of them, and the page degrades rather than erroring:
 *
 *   1. turns are inferred from `entry-node` entries (each one opens a turn);
 *   2. a skipped step is read off the node data: the engine writes a row with an
 *      empty input and no output for every step a branch skipped, and the branch
 *      that skipped it is inferred as the last branch step that completed before
 *      that row;
 *   3. there is no branch evaluation detail and no schema check; the output
 *      check innovate-service already records on `llm_call` metadata (task
 *      1139) stands in for the schema check where it exists.
 *
 * Every 1150 field is read here and nowhere else, so reconciling with the
 * shipped shapes is a change to this file's accessors.
 */
import type {
  CollaborationJournal,
  CollaborationNodeData,
  RunSchemaCheck,
} from '../types';
import type { CollaborationDefinition, CollaborationNode } from '../types/definition';
import type {
  BranchEvaluation,
  RunLink,
  RunOutputCheck,
  StepState,
  Turn,
  TurnStep,
} from '../types/runExplorer';

// ---------------------------------------------------------------------------
// Accessors for the task-1150 recordings (top-level field first, then metadata)
// ---------------------------------------------------------------------------

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const fromEntry = (entry: CollaborationJournal, key: string): unknown => {
  const top = (entry as unknown as Record<string, unknown>)[key];
  if (top !== undefined && top !== null) return top;
  return entry.metadata?.[key] ?? null;
};

/** The 0-based turn an entry belongs to, when the service recorded it. */
export const entryTurnIndex = (entry: CollaborationJournal): number | null => {
  const value = fromEntry(entry, 'turnIndex');
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
};

export const isSkippedEntry = (entry: CollaborationJournal): boolean =>
  entry.event === 'skipped' || entry.type === 'skipped';

/** The step an entry is about: the skipped step for a `skipped` entry, else the node. */
export const entryStepId = (entry: CollaborationJournal): string =>
  (isSkippedEntry(entry) ? readString(fromEntry(entry, 'stepId')) : null) ?? entry.nodeId;

export const schemaCheckOf = (entry: CollaborationJournal | null | undefined): RunSchemaCheck | null => {
  if (!entry) return null;
  const check = asRecord(fromEntry(entry, 'schemaCheck'));
  return check && typeof check.passed === 'boolean' ? (check as unknown as RunSchemaCheck) : null;
};

export const outputCheckOf = (entry: CollaborationJournal | null | undefined): RunOutputCheck | null => {
  const check = asRecord(entry?.metadata?.outputCheck);
  return check && typeof check.passed === 'boolean' ? (check as unknown as RunOutputCheck) : null;
};

/** A branch step's condition-by-condition record (1150), or null for a pre-1150 run. */
export const branchEvaluationsOf = (entry: CollaborationJournal | null | undefined): BranchEvaluation[] | null => {
  if (!entry) return null;
  const list = entry.output?.evaluations ?? entry.metadata?.evaluations;
  return Array.isArray(list) ? (list.filter((e) => asRecord(e)) as BranchEvaluation[]) : null;
};

/** The child run a tool call started, from the entry itself or the run's children list. */
export const childLinkOf = (entry: CollaborationJournal, children: RunLink[]): RunLink | null => {
  const listed = children.find((child) => child.toolCallEntryId === entry.id);
  if (listed) return listed;
  const runId = readString(fromEntry(entry, 'childRunId'));
  if (!runId) return null;
  const collaborationId = readString(fromEntry(entry, 'childCollaborationId')) ?? runId;
  return { collaborationId, runId, stepId: entry.nodeId, toolCallEntryId: entry.id };
};

/**
 * A skip worth reporting: the step was starved by an earlier skip
 * (`reason: 'upstream-skipped'`, task 1150), not routed around by a branch.
 * A routed run skips most of its graph on every turn, so a `branch` skip is
 * routine (task 1162).
 */
export const isUpstreamSkipEntry = (entry: CollaborationJournal): boolean =>
  isSkippedEntry(entry) && fromEntry(entry, 'reason') === 'upstream-skipped';

/**
 * Does this entry belong in the journal's Problems Only view? A failure, a
 * failed schema check, a skip that came from upstream (a routine branch skip
 * is not a problem, task 1162), a failed output check on a model call (task
 * 1139), and a tool call that returned an error.
 */
export const isProblemEntry = (entry: CollaborationJournal): boolean =>
  isUpstreamSkipEntry(entry) ||
  entry.event === 'failed' ||
  schemaCheckOf(entry)?.passed === false ||
  outputCheckOf(entry)?.passed === false ||
  (entry.event === 'tool_call' && !!entry.error);

// ---------------------------------------------------------------------------
// Small readers
// ---------------------------------------------------------------------------

const timeOf = (iso: string | null | undefined): number => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : NaN;
};

/** When an entry's work began: the time it was written, less its duration. */
export const entryStartMs = (entry: CollaborationJournal): number =>
  timeOf(entry.createdAt) - (typeof entry.duration === 'number' ? entry.duration : 0);

export const tokenCount = (usage: Record<string, unknown> | null | undefined, key: string): number => {
  const value = usage?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const isEmptyObject = (value: unknown): boolean => {
  const record = asRecord(value);
  return !!record && Object.keys(record).length === 0;
};

/** A node-data row the engine wrote for a step a branch skipped. */
const looksSkipped = (row: CollaborationNodeData): boolean =>
  row.output === null && (row.input === null || isEmptyObject(row.input));

const sortByTime = <T extends { createdAt: string }>(rows: T[]): T[] =>
  rows
    .map((row, order) => ({ row, order }))
    .sort((a, b) => timeOf(a.row.createdAt) - timeOf(b.row.createdAt) || a.order - b.order)
    .map(({ row }) => row);

const toIso = (ms: number): string | null => (Number.isFinite(ms) ? new Date(ms).toISOString() : null);

export const findEntryNodeId = (
  definition: CollaborationDefinition | null | undefined,
  journal: CollaborationJournal[]
): string | null =>
  definition?.nodes?.find((node) => node.type === 'entry')?.id ??
  definition?.nodes?.find((node) => node.entry)?.id ??
  journal.find((entry) => entry.nodeType === 'entry')?.nodeId ??
  null;

const userMessageOf = (value: unknown): string | null => {
  const record = asRecord(value);
  return readString(record?.message) ?? null;
};

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

function emptyStep(stepId: string, nodeType: string): TurnStep {
  return {
    stepId,
    nodeType,
    state: 'not-reached',
    startedAt: null,
    endedAt: null,
    durationMs: null,
    entries: [],
    modelCalls: [],
    toolCalls: [],
    resultEntry: null,
    schemaCheck: null,
    outputCheck: null,
    nodeData: null,
    skip: null,
    problems: [],
  };
}

function finishStep(step: TurnStep): void {
  if (step.entries.length === 0) return;
  const starts = step.entries.map(entryStartMs).filter(Number.isFinite);
  const ends = step.entries.map((entry) => timeOf(entry.createdAt)).filter(Number.isFinite);
  const start = starts.length ? Math.min(...starts) : NaN;
  const end = ends.length ? Math.max(...ends) : NaN;
  step.startedAt = toIso(start);
  step.endedAt = toIso(end);

  step.modelCalls = step.entries.filter((entry) => entry.event === 'llm_call');
  step.toolCalls = step.entries.filter((entry) => entry.event === 'tool_call');
  const failed = step.entries.filter((entry) => entry.event === 'failed');
  const completed = step.entries.filter((entry) => entry.event === 'completed');
  step.resultEntry = failed[failed.length - 1] ?? completed[completed.length - 1] ?? null;
  step.durationMs =
    typeof step.resultEntry?.duration === 'number'
      ? step.resultEntry.duration
      : Number.isFinite(start) && Number.isFinite(end)
        ? end - start
        : null;

  step.schemaCheck = schemaCheckOf(step.resultEntry);
  const checked = step.modelCalls.filter((call) => outputCheckOf(call));
  step.outputCheck = checked.length ? outputCheckOf(checked[checked.length - 1]) : null;

  const problems: string[] = [];
  if (step.schemaCheck?.passed === false) {
    const missing = step.schemaCheck.missingFields ?? [];
    problems.push(
      missing.length
        ? `Output does not match the schema (missing ${missing.join(', ')})`
        : 'Output does not match the schema'
    );
  } else if (!step.schemaCheck && step.outputCheck?.passed === false) {
    problems.push(
      step.outputCheck.reason === 'parse'
        ? 'Reply could not be read as one JSON object'
        : 'Reply failed the output check'
    );
  }
  for (const call of step.toolCalls) {
    if (call.error) problems.push(`Tool ${call.functionName ?? 'call'} returned an error`);
  }
  step.problems = problems;

  if (failed.length > 0) step.state = 'failed';
  else if (problems.length > 0) step.state = 'ran-with-problem';
  else step.state = 'ran';
}

export interface TurnModelInput {
  journal: CollaborationJournal[];
  nodeData: CollaborationNodeData[];
  definition: CollaborationDefinition | null | undefined;
  children?: RunLink[];
}

/**
 * Group a run's journal into turns and say, for each turn, what ran, what a
 * branch skipped and what was never reached.
 *
 * Accepts the positional form the task names, `buildTurnModel(journal,
 * nodeData, definition)`, with the run's children as an optional fourth.
 */
export function buildTurnModel(
  journal: CollaborationJournal[],
  nodeData: CollaborationNodeData[],
  definition: CollaborationDefinition | null | undefined,
  children: RunLink[] = []
): Turn[] {
  const entries = sortByTime(journal ?? []);
  const rows = sortByTime(nodeData ?? []);
  if (entries.length === 0 && rows.length === 0) return [];

  const definitionNodes: CollaborationNode[] = Array.isArray(definition?.nodes) ? definition!.nodes : [];
  const entryNodeId = findEntryNodeId(definition, entries);

  // 1. Turn of each journal entry.
  const recorded = entries.some((entry) => entryTurnIndex(entry) !== null);
  const entryTurn: number[] = [];
  if (recorded) {
    let last = 0;
    for (const entry of entries) {
      const index = entryTurnIndex(entry);
      if (index !== null) last = index;
      entryTurn.push(last);
    }
  } else {
    let current = -1;
    for (const entry of entries) {
      const opensTurn =
        (entry.nodeType === 'entry' || (entryNodeId !== null && entry.nodeId === entryNodeId)) &&
        entry.event !== 'waiting' &&
        !isSkippedEntry(entry);
      if (opensTurn) current += 1;
      entryTurn.push(Math.max(current, 0));
    }
  }
  const turnCount = Math.max(1, (entryTurn.length ? Math.max(...entryTurn) : 0) + 1);

  // 2. Turn of each node-data row: the entry node's row opens a turn; without
  //    one, the row joins the last turn that had started by its time.
  const turnStartMs: number[] = Array.from({ length: turnCount }, () => NaN);
  entries.forEach((entry, i) => {
    const t = entryTurn[i];
    const start = entryStartMs(entry);
    if (!Number.isFinite(turnStartMs[t]) || start < turnStartMs[t]) turnStartMs[t] = start;
  });
  const rowTurn: number[] = [];
  const entryRows = entryNodeId ? rows.filter((row) => row.nodeId === entryNodeId).length : 0;
  if (entryRows > 0) {
    let current = -1;
    for (const row of rows) {
      if (row.nodeId === entryNodeId) current += 1;
      rowTurn.push(Math.min(Math.max(current, 0), turnCount - 1));
    }
  } else {
    for (const row of rows) {
      const at = timeOf(row.createdAt);
      let t = 0;
      for (let i = 0; i < turnCount; i += 1) {
        if (Number.isFinite(turnStartMs[i]) && turnStartMs[i] <= at) t = i;
      }
      rowTurn.push(t);
    }
  }

  // 3. Build each turn.
  const turns: Turn[] = [];
  for (let t = 0; t < turnCount; t += 1) {
    const turnEntries = entries.filter((_, i) => entryTurn[i] === t);
    const turnRows = rows.filter((_, i) => rowTurn[i] === t);

    const steps: Record<string, TurnStep> = {};
    for (const node of definitionNodes) steps[node.id] = emptyStep(node.id, node.type);
    const stepFor = (stepId: string, nodeType: string): TurnStep => {
      if (!steps[stepId]) steps[stepId] = emptyStep(stepId, nodeType);
      return steps[stepId];
    };

    const skippedEntries: CollaborationJournal[] = [];
    for (const entry of turnEntries) {
      if (isSkippedEntry(entry)) {
        skippedEntries.push(entry);
        continue;
      }
      stepFor(entry.nodeId, entry.nodeType).entries.push(entry);
    }
    for (const step of Object.values(steps)) finishStep(step);

    for (const row of turnRows) {
      const step = stepFor(row.nodeId, definitionNodes.find((n) => n.id === row.nodeId)?.type ?? 'agent');
      // A step's own data row wins over a skip-shaped row for the same step.
      if (!step.nodeData || !looksSkipped(row)) step.nodeData = row;
    }

    // Skips recorded by the service (1150).
    for (const entry of skippedEntries) {
      const step = stepFor(entryStepId(entry), entry.nodeType);
      if (step.entries.length > 0) continue;
      step.state = 'skipped';
      step.skip = {
        branchStepId: readString(fromEntry(entry, 'branchStepId')),
        selectedBranch: readString(fromEntry(entry, 'selectedBranch')),
        reason: readString(fromEntry(entry, 'reason')),
        at: entry.createdAt,
        inferred: false,
        entry,
      };
    }

    // Skips read off the node data (pre-1150 runs).
    const branchResults = turnEntries.filter(
      (entry) => entry.nodeType === 'branch' && entry.event === 'completed'
    );
    for (const row of turnRows) {
      const step = steps[row.nodeId];
      if (!step || step.entries.length > 0 || step.skip || !looksSkipped(row)) continue;
      const at = timeOf(row.createdAt);
      const branch = [...branchResults].reverse().find((entry) => timeOf(entry.createdAt) <= at) ?? null;
      step.state = 'skipped';
      step.skip = {
        branchStepId: branch?.nodeId ?? null,
        selectedBranch: readString(branch?.output?.selectedBranch),
        reason: null,
        at: row.createdAt,
        inferred: true,
        entry: null,
      };
    }

    const ran = Object.values(steps).filter((step) => step.entries.length > 0);
    const path = ran
      .slice()
      .sort((a, b) => timeOf(a.startedAt) - timeOf(b.startedAt) || timeOf(a.endedAt) - timeOf(b.endedAt))
      .map((step) => step.stepId);
    const skipped = Object.values(steps)
      .filter((step) => step.state === 'skipped')
      .sort((a, b) => timeOf(a.skip?.at) - timeOf(b.skip?.at))
      .map((step) => step.stepId);
    const notReached = Object.values(steps)
      .filter((step) => step.state === 'not-reached')
      .map((step) => step.stepId);

    const modelCalls = turnEntries.filter((entry) => entry.event === 'llm_call');
    const toolCalls = turnEntries.filter((entry) => entry.event === 'tool_call');

    const childRuns: RunLink[] = [];
    for (const call of toolCalls) {
      const link = childLinkOf(call, children);
      if (link && !childRuns.some((c) => c.runId === link.runId)) childRuns.push(link);
    }

    const starts = turnEntries.map(entryStartMs).filter(Number.isFinite);
    const ends = turnEntries.map((entry) => timeOf(entry.createdAt)).filter(Number.isFinite);
    const startMs = starts.length ? Math.min(...starts) : NaN;
    const endMs = ends.length ? Math.max(...ends) : NaN;

    const entryStep = entryNodeId ? steps[entryNodeId] : undefined;
    const userMessage =
      userMessageOf(entryStep?.entries[0]?.input) ??
      userMessageOf(entryStep?.entries[0]?.output) ??
      userMessageOf(entryStep?.nodeData?.input) ??
      null;

    const agentPath = path.filter((id) => steps[id]?.nodeType === 'agent');
    const lastAgent = agentPath.length ? steps[agentPath[agentPath.length - 1]] : null;
    const failedStep = path.map((id) => steps[id]).find((step) => step.state === 'failed');
    const saveStep = path
      .map((id) => steps[id])
      .reverse()
      .find((step) => typeof step.resultEntry?.output?.saved === 'boolean');
    let summary = lastAgent ? lastAgent.stepId : 'no agent step';
    if (failedStep) summary = `${failedStep.stepId} failed`;
    else if (lastAgent?.state === 'ran-with-problem') summary = `${lastAgent.stepId} · reply unreadable`;
    if (saveStep && !failedStep) summary += saveStep.resultEntry!.output!.saved ? ' · saved' : ' · not saved';

    turns.push({
      index: t,
      startedAt: toIso(startMs),
      endedAt: toIso(endMs),
      durationMs: Number.isFinite(startMs) && Number.isFinite(endMs) ? endMs - startMs : null,
      userMessage,
      path,
      skipped,
      notReached,
      steps,
      entries: turnEntries,
      modelCallCount: modelCalls.length,
      toolCallCount: toolCalls.length,
      problemCallCount: modelCalls.filter((call) => outputCheckOf(call)?.passed === false).length +
        ran.filter((step) => step.schemaCheck?.passed === false).length,
      inputTokens: modelCalls.reduce((sum, call) => sum + tokenCount(call.usage, 'inputTokens'), 0),
      outputTokens: modelCalls.reduce((sum, call) => sum + tokenCount(call.usage, 'outputTokens'), 0),
      cacheReadTokens: modelCalls.reduce((sum, call) => sum + tokenCount(call.usage, 'cacheReadTokens'), 0),
      cacheWriteTokens: modelCalls.reduce((sum, call) => sum + tokenCount(call.usage, 'cacheWriteTokens'), 0),
      modelTimeMs: modelCalls.reduce((sum, call) => sum + (call.duration ?? 0), 0),
      childRuns,
      specialists: agentPath,
      outcome: {
        stepId: failedStep?.stepId ?? lastAgent?.stepId ?? null,
        summary,
        problem: !!failedStep || lastAgent?.state === 'ran-with-problem',
      },
      inferred: !recorded,
    });
  }
  return turns;
}

// ---------------------------------------------------------------------------
// Run-level summary (the header's summary strip)
// ---------------------------------------------------------------------------

export interface RunSummary {
  stepsRan: number;
  stepsTotal: number;
  stepsSkipped: number;
  modelCalls: number;
  problemCalls: number;
  toolCalls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  modelTimeMs: number;
  wallMs: number | null;
  childRuns: number;
  artifacts: string[];
}

/**
 * The summary strip, all turns combined. Steps are counted once however many
 * turns ran them; a step counts as skipped only if no turn ran it.
 */
export function summarizeRun(
  turns: Turn[],
  definition: CollaborationDefinition | null | undefined,
  children: RunLink[] = []
): RunSummary {
  const ranIds = new Set<string>();
  const skippedIds = new Set<string>();
  const seenIds = new Set<string>((definition?.nodes ?? []).map((node) => node.id));
  const childIds = new Set<string>(children.map((child) => child.runId));
  const artifacts: string[] = [];

  for (const turn of turns) {
    turn.path.forEach((id) => ranIds.add(id));
    turn.skipped.forEach((id) => skippedIds.add(id));
    Object.keys(turn.steps).forEach((id) => seenIds.add(id));
    turn.childRuns.forEach((child) => childIds.add(child.runId));
    for (const id of turn.path) {
      const step = turn.steps[id];
      // What the run created: every create tool call that succeeded, and every
      // save step that saved.
      for (const call of step.toolCalls) {
        if (!call.error && call.functionName && /_create$/.test(call.functionName)) {
          artifacts.push(call.functionName.replace(/_create$/, '').replace(/_/g, ' '));
        }
      }
      if (step.resultEntry?.output?.saved === true) artifacts.push(id);
    }
  }
  ranIds.forEach((id) => skippedIds.delete(id));

  const starts = turns.map((turn) => timeOf(turn.startedAt)).filter(Number.isFinite);
  const ends = turns.map((turn) => timeOf(turn.endedAt)).filter(Number.isFinite);

  return {
    stepsRan: ranIds.size,
    stepsTotal: seenIds.size,
    stepsSkipped: skippedIds.size,
    modelCalls: turns.reduce((sum, turn) => sum + turn.modelCallCount, 0),
    problemCalls: turns.reduce((sum, turn) => sum + turn.problemCallCount, 0),
    toolCalls: turns.reduce((sum, turn) => sum + turn.toolCallCount, 0),
    inputTokens: turns.reduce((sum, turn) => sum + turn.inputTokens, 0),
    outputTokens: turns.reduce((sum, turn) => sum + turn.outputTokens, 0),
    cacheReadTokens: turns.reduce((sum, turn) => sum + turn.cacheReadTokens, 0),
    cacheWriteTokens: turns.reduce((sum, turn) => sum + turn.cacheWriteTokens, 0),
    modelTimeMs: turns.reduce((sum, turn) => sum + turn.modelTimeMs, 0),
    wallMs: starts.length && ends.length ? Math.max(...ends) - Math.min(...starts) : null,
    childRuns: childIds.size,
    artifacts,
  };
}

// ---------------------------------------------------------------------------
// Mappings (inputs and downstream consumers)
// ---------------------------------------------------------------------------

const REFERENCE = /\{\{\s*([\w-]+)\.output(?:\.([\w.-]+))?\s*\}\}/;

/** `{{step.output.field}}` → `{ stepId, field }`, or null for anything else. */
export const parseReference = (value: unknown): { stepId: string; field: string | null } | null => {
  if (typeof value !== 'string') return null;
  const match = REFERENCE.exec(value);
  return match ? { stepId: match[1], field: match[2] ?? null } : null;
};

export interface OutputConsumer {
  /** The output field read, or null when the whole output is read. */
  field: string | null;
  /** The step that reads it, or null for the run's own output mapping (the reply). */
  consumerStepId: string | null;
  /** The input field (or reply field) the value lands in. */
  target: string;
}

/** Who reads a step's output: later steps' input mappings, and the run's output mapping. */
export function outputConsumers(
  definition: CollaborationDefinition | null | undefined,
  stepId: string,
  outputMapping?: Record<string, string> | null
): OutputConsumer[] {
  const consumers: OutputConsumer[] = [];
  for (const node of definition?.nodes ?? []) {
    for (const [target, value] of Object.entries(node.inputMapping ?? {})) {
      const ref = parseReference(value);
      if (ref?.stepId === stepId) consumers.push({ field: ref.field, consumerStepId: node.id, target });
    }
  }
  for (const [target, value] of Object.entries(outputMapping ?? {})) {
    const ref = parseReference(value);
    if (ref?.stepId === stepId) consumers.push({ field: ref.field, consumerStepId: null, target });
  }
  return consumers;
}

/** Edges whose both ends ran in the turn: the ones that carried data. */
export const ranEdgeKey = (from: string, to: string): string => `${from}->${to}`;

export function stateCounts(turn: Turn): Record<StepState, number> {
  const counts: Record<StepState, number> = {
    ran: 0,
    'ran-with-problem': 0,
    failed: 0,
    skipped: 0,
    'not-reached': 0,
  };
  for (const step of Object.values(turn.steps)) counts[step.state] += 1;
  return counts;
}
